import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import Input from '@/components/ui/Input';
import { dataExportApi } from '@/services/dataExport';
import { settingsApi } from '@/services/settings';
import { useAuthStore } from '@/store/authStore';
import { config } from '@/config';
import type { DataExportRequest, DataExportStatus } from '@/types/dataExport';
import type { OAuthProvider } from '@/types';
import { ApiClientError } from '@/services/api';
import { toast } from '@/components/ui/Toaster';

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return 'Unknown';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get status badge styling
 */
function getStatusBadge(status: DataExportStatus): { label: string; className: string } {
  switch (status) {
    case 'pending':
      return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    case 'processing':
      return { label: 'Processing', className: 'bg-blue-100 text-blue-800' };
    case 'ready':
      return { label: 'Ready', className: 'bg-green-100 text-green-800' };
    case 'failed':
      return { label: 'Failed', className: 'bg-red-100 text-red-800' };
    case 'expired':
      return { label: 'Expired', className: 'bg-gray-100 text-gray-800' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-800' };
  }
}

/**
 * Get display name for OAuth provider
 * WP-GDPR-EXPORT-2: Helper for OAuth re-authentication UI
 */
function getProviderDisplayName(provider: OAuthProvider): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    case 'apple':
      return 'Apple';
    default:
      return provider;
  }
}

/**
 * Privacy & Data Settings Component
 * Allows users to request and download their personal data (GDPR Article 15)
 */
export default function PrivacyDataSettings() {
  // WP-GDPR-EXPORT-2: Get user for OAuth detection
  const { user } = useAuthStore();

  // Export list state
  const [exports, setExports] = useState<DataExportRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Security status for MFA check
  const [hasMfa, setHasMfa] = useState<boolean | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'confirm' | 'auth'>('confirm');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // WP-GDPR-EXPORT-2: OAuth verification state for OAuth-only users
  const [oauthVerified, setOauthVerified] = useState<{
    idToken: string;
    provider: OAuthProvider;
  } | null>(null);

  // Download state
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // URL query parameters for email link support
  const [searchParams, setSearchParams] = useSearchParams();
  const downloadExportId = searchParams.get('download');
  const hasTriggeredAutoDownload = useRef(false);

  // WP-GDPR-EXPORT-2: Determine if user is OAuth-only (no password)
  const isOAuthOnly = user?.hasPassword === false;
  const primaryProvider = user?.primaryAuthMethod as OAuthProvider | undefined;

  // Check if Google OAuth is configured
  const isGoogleConfigured = Boolean(config.googleClientId);

  // Fetch exports and security status
  useEffect(() => {
    fetchExports();
    fetchSecurityStatus();
  }, []);

  const fetchExports = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dataExportApi.listExports(10);
      setExports(response.exports);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to load export history');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSecurityStatus = async () => {
    try {
      const status = await settingsApi.getSecurityStatus();
      setHasMfa(status.mfaEnabled);
    } catch {
      // Default to password-based auth if we can't determine
      setHasMfa(false);
    }
  };

  // Polling for in-progress exports
  useEffect(() => {
    const hasInProgress = exports.some(
      (e) => e.status === 'pending' || e.status === 'processing'
    );

    if (hasInProgress) {
      const interval = setInterval(() => {
        fetchExports();
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [exports]);

  const handleDownload = useCallback(async (exportId: string) => {
    setDownloadingId(exportId);
    try {
      const response = await dataExportApi.getDownloadUrl(exportId);
      // Open download URL in new tab
      window.open(response.downloadUrl, '_blank');
      // Refresh exports to update download count
      fetchExports();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 410) {
          toast.error('This export has expired or reached the download limit.');
        } else {
          toast.error(err.detail || 'Failed to get download link');
        }
      } else {
        toast.error('Failed to download export');
      }
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Auto-trigger download from email link (query parameter: ?download=<export_id>)
  useEffect(() => {
    // Skip if no download parameter, already triggered, or still loading
    if (!downloadExportId || hasTriggeredAutoDownload.current || isLoading) {
      return;
    }

    // Find the export in the list
    const targetExport = exports.find((e) => e.id === downloadExportId);

    if (targetExport) {
      hasTriggeredAutoDownload.current = true;

      // Clear the query parameter to prevent re-triggering
      setSearchParams({}, { replace: true });

      if (targetExport.isDownloadable) {
        // Trigger download
        handleDownload(downloadExportId);
      } else if (targetExport.status === 'pending' || targetExport.status === 'processing') {
        toast.info('Your data export is still being prepared. Please wait.');
      } else if (targetExport.status === 'expired') {
        toast.error('This data export has expired. Please request a new export.');
      } else if (targetExport.status === 'failed') {
        toast.error('This data export failed. Please request a new export.');
      }
    } else if (exports.length > 0) {
      // Exports loaded but target not found
      hasTriggeredAutoDownload.current = true;
      setSearchParams({}, { replace: true });
      toast.error('Export not found. It may have expired or been deleted.');
    }
  }, [downloadExportId, exports, isLoading, handleDownload, setSearchParams]);

  const handleRequestExport = () => {
    setModalStep('confirm');
    setPassword('');
    setMfaCode('');
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleConfirmRequest = () => {
    setModalStep('auth');
    setModalError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalStep('confirm');
    setPassword('');
    setMfaCode('');
    setModalError(null);
    setOauthVerified(null);
  };

  // WP-GDPR-EXPORT-2: Handle Google OAuth re-authentication success
  const handleGoogleSuccess = useCallback((credentialResponse: CredentialResponse) => {
    const idToken = credentialResponse.credential;
    if (idToken) {
      setOauthVerified({ idToken, provider: 'google' });
      setModalError(null);
    } else {
      setModalError('Failed to get credentials from Google. Please try again.');
    }
  }, []);

  // WP-GDPR-EXPORT-2: Handle Google OAuth error
  const handleGoogleError = useCallback(() => {
    setModalError('Google sign-in was cancelled or failed. Please try again.');
  }, []);

  const handleSubmitExport = async () => {
    setIsSubmitting(true);
    setModalError(null);

    try {
      // WP-GDPR-EXPORT-2: Build request based on user's auth method
      let request;
      if (isOAuthOnly && oauthVerified) {
        // OAuth verification for OAuth-only users
        request = {
          oauthIdToken: oauthVerified.idToken,
          oauthProvider: oauthVerified.provider,
        };
      } else if (hasMfa) {
        // MFA verification for MFA-enabled users
        request = { mfaCode };
      } else {
        // Password verification for password users
        request = { password };
      }

      await dataExportApi.initiateExport(request);
      toast.success('Data export request submitted. You will receive an email when your data is ready.');
      handleCloseModal();
      fetchExports();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          setModalError('You can only request one data export per week. Please try again later.');
        } else if (err.status === 400) {
          setModalError(err.detail || 'Authentication failed. Please check your credentials.');
        } else {
          setModalError(err.detail || 'Failed to initiate export');
        }
      } else {
        setModalError('An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canRequestExport = !exports.some(
    (e) => e.status === 'pending' || e.status === 'processing'
  );

  if (isLoading && exports.length === 0) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Privacy & Data"
          description="Manage your personal data and privacy settings"
        />

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="space-y-6">
          {/* Download Your Data Section */}
          <div className="border-b border-gray-100 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Download Your Data</h3>
            <p className="text-sm text-gray-600 mb-4">
              Request a copy of all your personal data stored in FlowIO. This includes your profile
              information, financial accounts, transactions, and any other data we have about you.
              Your data will be compiled into a downloadable ZIP file.
            </p>
            <div className="flex items-center gap-4">
              <Button
                onClick={handleRequestExport}
                disabled={!canRequestExport}
              >
                Request Data Export
              </Button>
              {!canRequestExport && (
                <span className="text-sm text-gray-500">
                  An export is already in progress
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Note: Data exports are limited to one request per week. Download links expire after 48 hours.
            </p>
          </div>

          {/* Export History */}
          {exports.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Export History</h3>
              <div className="space-y-3">
                {exports.map((exp) => {
                  const statusBadge = getStatusBadge(exp.status);
                  return (
                    <div
                      key={exp.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}
                          >
                            {statusBadge.label}
                          </span>
                          {exp.fileSizeBytes && (
                            <span className="text-xs text-gray-500">
                              {formatFileSize(exp.fileSizeBytes)}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          Requested {formatDate(exp.requestedAt)}
                        </div>
                        {exp.status === 'ready' && exp.expiresAt && (
                          <div className="text-xs text-gray-400">
                            Expires {formatDate(exp.expiresAt)} &bull; {3 - exp.downloadCount} downloads remaining
                          </div>
                        )}
                        {exp.status === 'failed' && exp.errorMessage && (
                          <div className="text-xs text-red-500">
                            {exp.errorMessage}
                          </div>
                        )}
                      </div>
                      <div>
                        {exp.isDownloadable && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(exp.id)}
                            disabled={downloadingId === exp.id}
                          >
                            {downloadingId === exp.id ? (
                              <Spinner size="sm" />
                            ) : (
                              'Download'
                            )}
                          </Button>
                        )}
                        {(exp.status === 'pending' || exp.status === 'processing') && (
                          <span className="text-sm text-gray-500">
                            <Spinner size="sm" className="inline-block mr-2" />
                            Processing...
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data Retention Info */}
          <div className="border-t border-gray-100 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Data Retention</h3>
            <p className="text-sm text-gray-600">
              We retain your data as long as your account is active. If you wish to delete your
              account and all associated data, you can do so from the Profile settings page.
              Please note that some data may be retained for legal or regulatory compliance.
            </p>
          </div>
        </div>
      </Card>

      {/* Request Export Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-modal-title"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={modalStep === 'confirm' ? handleCloseModal : undefined}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 id="export-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
              {modalStep === 'confirm' ? 'Request Data Export' : 'Verify Your Identity'}
            </h2>

            {modalStep === 'confirm' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  You are about to request a complete export of your personal data. This process may
                  take a few minutes to complete. Once ready, you will receive an email with a secure
                  download link.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">What&apos;s included:</h4>
                  <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                    <li>Your profile and account information</li>
                    <li>Financial accounts and balances</li>
                    <li>Transaction history</li>
                    <li>Categories and flow data</li>
                    <li>Connected bank information</li>
                    <li>Subscription and billing history</li>
                  </ul>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button onClick={handleConfirmRequest}>
                    Continue
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmitExport();
                }}
                className="space-y-4"
              >
                <p className="text-sm text-gray-600">
                  For your security, please verify your identity to proceed with the data export request.
                </p>

                {modalError && (
                  <Alert variant="error" onDismiss={() => setModalError(null)}>
                    {modalError}
                  </Alert>
                )}

                {/* WP-GDPR-EXPORT-2: Show OAuth, MFA, or password based on user's auth method */}
                {isOAuthOnly ? (
                  // OAuth re-authentication for OAuth-only users
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      Please verify your identity by signing in with{' '}
                      {primaryProvider ? getProviderDisplayName(primaryProvider) : 'your account'}:
                    </p>

                    {oauthVerified ? (
                      // Show verified state
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-green-700">
                          Identity verified with {getProviderDisplayName(oauthVerified.provider)}
                        </span>
                      </div>
                    ) : (
                      // Show OAuth buttons
                      <div className="space-y-2">
                        {/* Google re-auth */}
                        {(primaryProvider === 'google' || !primaryProvider) && isGoogleConfigured && (
                          <div className="w-full">
                            <GoogleLogin
                              onSuccess={handleGoogleSuccess}
                              onError={handleGoogleError}
                              useOneTap={false}
                              theme="outline"
                              size="large"
                              width={352}
                              text="continue_with"
                              shape="rectangular"
                            />
                          </div>
                        )}

                        {/* Show message for other providers (Microsoft/Apple) */}
                        {primaryProvider && primaryProvider !== 'google' && (
                          <p className="text-sm text-gray-500 text-center">
                            {getProviderDisplayName(primaryProvider)} re-authentication coming soon.
                            Please contact support for assistance.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : hasMfa ? (
                  // MFA verification for MFA-enabled users
                  <Input
                    label="Authenticator Code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit code"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                ) : (
                  // Password verification for password users
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    autoFocus
                  />
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModalStep('confirm')}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (isOAuthOnly
                        ? !oauthVerified
                        : hasMfa
                        ? mfaCode.length !== 6
                        : !password)
                    }
                    isLoading={isSubmitting}
                  >
                    Request Export
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
